import { intlLocale, t } from "@/lib/i18n"
import { parseDecimal } from "@/lib/formatters"
import type { Granularity, StatsBucket } from "@/features/staff/stats/types"

/**
 * Display helpers for the stats screen.
 *
 * **Nothing here derives a peso figure.** Two functions parse a decimal string —
 * `barPercent` to compute a CSS width and `seriesExtent` to find the tallest
 * point — and both return layout, never a number anyone reads. Every amount on
 * screen is the exact string the server sent.
 *
 * The copy lives under `ui.staff.stats.*` rather than under the page's own
 * `ui.react.pages.*` branch, and that is deliberate: the serializer already
 * resolves the range names and the leak labels from there, and this screen's
 * whole job is to state what each figure does and does not reconcile with. Two
 * Spanish copies of "no cuadra con ventas netas" is one copy too many — the
 * reports screen makes the same call for the same reason.
 */
export function tStats(key: string, options: Record<string, string | number> = {}) {
  return t(`ui.staff.stats.${key}`, options)
}

/** A growth ratio as a signed percentage. `null` renders as "nuevo". */
export function formatGrowth(ratio: number | null | undefined): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) {
    return tStats("kpi.new")
  }

  try {
    return new Intl.NumberFormat(intlLocale(), {
      style: "percent",
      maximumFractionDigits: 1,
      signDisplay: "exceptZero",
    }).format(ratio)
  } catch {
    return `${Math.round(ratio * 1000) / 10}%`
  }
}

export function formatShare(share: number | null | undefined): string {
  if (share === null || share === undefined || !Number.isFinite(share)) return "—"

  try {
    return new Intl.NumberFormat(intlLocale(), {
      style: "percent",
      maximumFractionDigits: 1,
    }).format(share)
  } catch {
    return `${Math.round(share * 1000) / 10}%`
  }
}

export type GrowthTone = "up" | "down" | "flat" | "new"

/**
 * Direction of travel, never a verdict. "Up" is `success` and "down" is
 * `danger` as directions only — for a leak the caller inverts, because a
 * discount line that shrank is good news wearing a minus sign.
 */
export function growthTone(ratio: number | null | undefined): GrowthTone {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return "new"
  if (Math.abs(ratio) < 0.001) return "flat"
  return ratio > 0 ? "up" : "down"
}

/** The tallest closed point, as a number, for scaling only. */
export function seriesExtent(buckets: StatsBucket[]): number {
  return buckets.reduce((max, bucket) => Math.max(max, parseDecimal(bucket.subtotal)), 0)
}

export function barPercent(value: string, max: number): number {
  if (max <= 0) return 0
  const parsed = parseDecimal(value)
  if (!Number.isFinite(parsed) || parsed <= 0) return 0
  return Math.min(100, Math.round((parsed / max) * 1000) / 10)
}

/**
 * A bucket's axis label, in the restaurant's zone.
 *
 * The date arrives as a bare ISO date because a bucket is a calendar label, not
 * an instant. It is parsed as UTC noon rather than midnight so no zone west of
 * Greenwich can pull it back a day — the value has already been resolved in the
 * restaurant's clock server-side and must not move again here.
 */
export function bucketLabel(date: string, granularity: Granularity): string {
  const parsed = new Date(`${date}T12:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return date

  const options: Intl.DateTimeFormatOptions =
    granularity === "month"
      ? { month: "short", year: "2-digit", timeZone: "UTC" }
      : { day: "numeric", month: "short", timeZone: "UTC" }

  try {
    return new Intl.DateTimeFormat(intlLocale(), options).format(parsed)
  } catch {
    return date
  }
}

export function formatHourLabel(hour: number | null | undefined): string {
  if (hour === null || hour === undefined) return "—"
  return `${String(hour).padStart(2, "0")}:00`
}

/** ISO weekday (1 = Monday) as a short localized name. */
export function weekdayLabel(weekday: number): string {
  // 2024-01-01 was a Monday, so ISO weekday N is that date plus N-1 days.
  const parsed = new Date(Date.UTC(2024, 0, weekday))
  try {
    return new Intl.DateTimeFormat(intlLocale(), { weekday: "short", timeZone: "UTC" }).format(parsed)
  } catch {
    return String(weekday)
  }
}
